/* eslint-disable max-len */
import { Box, Button, createStyles, makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import _ from 'lodash';
import React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'themed-components';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const StyledDocument = styled(Document)`
	background-color: #2d3436;
	display: flex;
	justify-content: center;
	align-items: center;
	width: auto;
	overflow-x: auto;

	@media (min-width: 768px) {
		min-height: 54rem;
	}
`;
const StyledPdfPickerPaper = styled(Document)`
	display: flex;
	background-color: #1e272e;
	align-items: center;
	overflow-x: auto;
	min-height: 8rem;
`;

const StyledPage = styled(Page)`
	display: flex;
	justify-content: center;
`;

interface PDFThumbnailsProps {
	url: string;
	pageCount: number;
	activePage: number;
	setPageNumber: React.Dispatch<React.SetStateAction<number>>;
}

interface PdfBoxProps {
	onClick: (n: number) => void;
	pageNumber: number;
	activePage: number;
}
const useStyles = makeStyles<Theme, { isActive: boolean }>((theme) =>
	createStyles({
		root: (props) => {
			let color = 'rgba(0,0,0,0)';
			if (props.isActive) color = theme.palette.primary.light;

			return {
				margin: '0.25rem',
				boxSizing: 'border-box',
				border: `3px solid ${color}`
			};
		}
	})
);

const PdfBox = ({ onClick, pageNumber, activePage }: PdfBoxProps) => {
	const isActive = activePage == pageNumber;
	const classes = useStyles({ isActive });
	return (
		<Box className={classes.root}>
			<Button onClick={() => onClick(pageNumber)}>
				<Page scale={0.1} height={1000} pageNumber={pageNumber} />
			</Button>
			<Typography style={{ textAlign: 'center', color: '#dfe6e9' }} component="div">
				{pageNumber}
			</Typography>
		</Box>
	);
};

const Thumbnails = ({ url, pageCount, activePage, setPageNumber }: PDFThumbnailsProps) => {
	const pages = _.range(1, pageCount);
	return (
		<StyledPdfPickerPaper file={url}>
			{_.map(pages, (n) => (
				<PdfBox key={n} pageNumber={n} activePage={activePage} onClick={(x) => setPageNumber(x)} />
			))}
		</StyledPdfPickerPaper>
	);
};

const StyledSkeleton = styled.div`
	background-color: rgba(0, 0, 0, 0);
	padding: 0.5rem;
`;

const LoadingSkeleton = ({ refetch }: { refetch: () => Promise<any> }) => (
	<>
		<StyledSkeleton>
			<Skeleton variant="rect" height="6.5rem" width="100%" />
		</StyledSkeleton>
		<StyledSkeleton>
			<Skeleton variant="rect" height="2rem" width="100%" />
		</StyledSkeleton>
		<StyledSkeleton>
			<Skeleton variant="rect" height="16rem" width="100%" />
		</StyledSkeleton>
		<StyledSkeleton>
			<Skeleton variant="rect" height="27rem" width="100%" />
		</StyledSkeleton>
	</>
);

export interface PdfDisplayProps {
	url?: string;
	numPages: number;
	pageNumber: number;
	setPageNumber: React.Dispatch<React.SetStateAction<number>>;
	onLoaded: ({ numPages }: any) => void;
	documentLoading: boolean;
	refetch: () => Promise<any>;
}

export const PdfDisplay = ({ url, numPages, pageNumber, setPageNumber, refetch, documentLoading, onLoaded }: PdfDisplayProps) => (
	<Paper variant="elevation" elevation={10}>
		<Thumbnails url={url!} pageCount={numPages} activePage={pageNumber} setPageNumber={setPageNumber} />
		{documentLoading && <LoadingSkeleton refetch={refetch} />}
		{!documentLoading && (
			<StyledDocument file={url} onLoadSuccess={onLoaded}>
				<StyledPage pageNumber={pageNumber} />
			</StyledDocument>
		)}
	</Paper>
);
